/**
 * Pure layout engine for the Interactive Guide's horizontal mind map.
 * Given a (filtered) catalog it produces positioned nodes + connector
 * edges for a left-to-right Color → Growth form → Coral tree. No DOM —
 * the SVG renderer consumes this geometry, and tests assert on it.
 */
import { COLOR_TAGS, GROWTH_FORMS, type ColorTag, type Coral, type GrowthForm } from './types';

export type MindMapNodeKind = 'color' | 'growth' | 'coral';

export interface MindMapNode {
  readonly kind: MindMapNodeKind;
  readonly id: string;
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly color: ColorTag;
  readonly coralId?: string;
  readonly growthForm?: GrowthForm;
}

export interface MindMapEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
}

export interface MindMapLayout {
  readonly nodes: readonly MindMapNode[];
  readonly edges: readonly MindMapEdge[];
  readonly width: number;
  readonly height: number;
}

// ── Layout constants (px, pre-zoom) ─────────────────────────────────
const COL0_X = 8;
const COL1_X = 280;
const COL2_X = 560;
const W_COLOR = 132;
const W_GROWTH = 152;
const W_CORAL = 250;
const H_NODE = 44;
const SLOT_H = 58;
const GROWTH_GAP = 16;
const COLOR_GAP = 30;
const PAD = 12;

export const MIND_MAP_NODE_WIDTHS = { W_COLOR, W_GROWTH, W_CORAL } as const;

function midpoint(values: readonly number[]): number {
  const first = values[0];
  const last = values[values.length - 1];
  if (first === undefined || last === undefined) return 0;
  return (first + last) / 2;
}

/**
 * Build the mind-map geometry. A coral appears once under each color it
 * carries, so a multi-color coral shows up on multiple branches (matching
 * the original guide's behavior).
 */
export function buildMindMapLayout(corals: readonly Coral[]): MindMapLayout {
  const nodes: MindMapNode[] = [];
  const edges: MindMapEdge[] = [];
  let cursorY = PAD;

  for (const color of COLOR_TAGS) {
    const inColor = corals.filter((c) => c.colors.includes(color));
    if (inColor.length === 0) continue;

    const colorId = `color:${color}`;
    const growthCenters: number[] = [];
    const presentForms: GrowthForm[] = [];

    for (const form of GROWTH_FORMS) {
      const inForm = inColor
        .filter((c) => c.growthForm === form)
        .sort((a, b) => a.name.localeCompare(b.name));
      if (inForm.length === 0) continue;

      const growthId = `growth:${color}:${form}`;
      const coralCenters: number[] = [];

      for (const coral of inForm) {
        const centerY = cursorY + SLOT_H / 2;
        coralCenters.push(centerY);
        const coralNodeId = `coral:${color}:${coral.id}`;
        nodes.push({
          kind: 'coral',
          id: coralNodeId,
          label: coral.name,
          x: COL2_X,
          y: centerY - H_NODE / 2,
          w: W_CORAL,
          h: H_NODE,
          color,
          coralId: coral.id,
        });
        edges.push({ id: `${growthId}->${coralNodeId}`, from: growthId, to: coralNodeId });
        cursorY += SLOT_H;
      }

      const growthCenter = midpoint(coralCenters);
      growthCenters.push(growthCenter);
      presentForms.push(form);
      nodes.push({
        kind: 'growth',
        id: growthId,
        label: form,
        x: COL1_X,
        y: growthCenter - H_NODE / 2,
        w: W_GROWTH,
        h: H_NODE,
        color,
        growthForm: form,
      });
      cursorY += GROWTH_GAP;
    }

    const colorCenter = midpoint(growthCenters);
    nodes.push({
      kind: 'color',
      id: colorId,
      label: color,
      x: COL0_X,
      y: colorCenter - H_NODE / 2,
      w: W_COLOR,
      h: H_NODE,
      color,
    });
    for (const form of presentForms) {
      const growthId = `growth:${color}:${form}`;
      edges.push({ id: `${colorId}->${growthId}`, from: colorId, to: growthId });
    }
    cursorY += COLOR_GAP;
  }

  const width = COL2_X + W_CORAL + PAD;
  const height = Math.max(cursorY, SLOT_H + PAD);
  return { nodes, edges, width, height };
}
